import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { openFile } from "@/lib/fileViewer";
import { Link, useNavigate } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import StudentAvatar from "@/components/StudentAvatar";
import { fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import StudentFormDialog from "@/components/StudentFormDialog";
import { Plus, Search, MoreHorizontal, Pencil, Trash2, Eye, GraduationCap, KeyRound, Download } from "lucide-react";
import { toast } from "sonner";
import { YEAR_LABEL, TRACK_SHORT } from "@/lib/school";

const STATUS_STYLES = {
  attivo:     "bg-emerald-50 text-emerald-800 border border-emerald-200",
  sospeso:    "bg-orange-50 text-orange-800 border border-orange-200",
  ritirato:   "bg-stone-200 text-stone-700 border border-stone-300",
  diplomato:  "bg-indigo-50 text-indigo-800 border border-indigo-200",
};
const STATUS_LABEL = {
  attivo: "Attivo", sospeso: "Sospeso", ritirato: "Ritirato", diplomato: "Diplomato",
};

function InsuranceBadge({ ok, testId }) {
  return (
    <Badge data-testid={testId} className={`rounded-full text-[10px] font-medium border ${ok ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}`}>
      {ok ? "Assicurato" : "Senza assicurazione"}
    </Badge>
  );
}

function StudentStatus({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.attivo;
  return (
    <Badge className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium tracking-wide ${s}`}>
      {STATUS_LABEL[status] || status}
    </Badge>
  );
}

function fullName(s) {
  return `${s.last_name || ""} ${s.first_name || ""}`.trim() || "—";
}

export default function Students({ track }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [yearFilter, setYearFilter] = useState("all");
  const [trackFilter, setTrackFilter] = useState(track || "all");
  const [genderFilter, setGenderFilter] = useState("all");
  const [insFilter, setInsFilter] = useState("all");
  const [sortBy, setSortBy] = useState("year_name");

  const [createOpen, setCreateOpen] = useState(false);
  const [editStudent, setEditStudent] = useState(null);
  const [deleteStudent, setDeleteStudent] = useState(null);
  const [activated, setActivated] = useState(null); // {email, temp_password}

  const { data: students = [], isLoading } = useQuery({
    queryKey: ["students", { search, statusFilter, yearFilter, trackFilter, genderFilter, insFilter }],
    queryFn: async () => {
      const params = {};
      if (insFilter !== "all") params.has_insurance = insFilter === "si";
      if (search) params.search = search;
      if (statusFilter !== "all") params.status = statusFilter;
      if (yearFilter !== "all") params.year_level = yearFilter;
      if (trackFilter !== "all") params.track_type = trackFilter;
      if (genderFilter !== "all") params.gender = genderFilter;
      return (await api.get("/students", { params })).data;
    },
  });

  function exportPdf() {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (statusFilter !== "all") params.set("status", statusFilter);
    if (yearFilter !== "all") params.set("year_level", yearFilter);
    if (trackFilter !== "all") params.set("track_type", trackFilter);
    if (genderFilter !== "all") params.set("gender", genderFilter);
    params.set("sort", sortBy);
    const token = localStorage.getItem("token");
    if (token) params.set("auth", token);
    const base = process.env.REACT_APP_BACKEND_URL;
    openFile(`${base}/api/students/-export.pdf?${params.toString()}`, "Studenti.pdf");
  }

  const { data: stats } = useQuery({
    queryKey: ["students-stats", track || "all"],
    queryFn: async () => (await api.get("/students-stats/overview", { params: track ? { track_type: track } : {} })).data,
  });

  const createMut = useMutation({
    mutationFn: async (payload) => (await api.post("/students", payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["students"] });
      qc.invalidateQueries({ queryKey: ["students-stats"] });
      toast.success("Studente creato");
      setCreateOpen(false);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const editMut = useMutation({
    mutationFn: async ({ id, payload }) => (await api.patch(`/students/${id}`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["students"] });
      qc.invalidateQueries({ queryKey: ["students-stats"] });
      toast.success("Studente aggiornato");
      setEditStudent(null);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const deleteMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/students/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["students"] });
      qc.invalidateQueries({ queryKey: ["students-stats"] });
      toast.success("Studente eliminato");
      setDeleteStudent(null);
    },
    onError: (e) => {
      toast.error(e.response?.data?.detail || "Errore eliminazione");
      setDeleteStudent(null);
    },
  });

  const activateMut = useMutation({
    mutationFn: async (id) => (await api.post("/student-accounts/activate", { student_id: id, send_email: true })).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["students"] });
      toast.success(`Account attivato per ${d.email}`);
      setActivated(d);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore attivazione"),
  });

  const countByStatus = (k) => stats?.by_status?.[k] || 0;

  // Ordinamento client-side (compound). Cognome+Nome case-insensitive, italiano.
  const sortedStudents = useMemo(() => {
    const list = [...students];
    const byName = (a, b) => {
      const an = `${a.last_name || ""} ${a.first_name || ""}`.trim().toLowerCase();
      const bn = `${b.last_name || ""} ${b.first_name || ""}`.trim().toLowerCase();
      return an.localeCompare(bn, "it", { sensitivity: "base" });
    };
    const byFirstLast = (a, b) => {
      const an = `${a.first_name || ""} ${a.last_name || ""}`.trim().toLowerCase();
      const bn = `${b.first_name || ""} ${b.last_name || ""}`.trim().toLowerCase();
      return an.localeCompare(bn, "it", { sensitivity: "base" });
    };
    const byYear = (a, b) => {
      const ay = Number(a.year_level ?? 99);
      const by = Number(b.year_level ?? 99);
      return ay - by;
    };
    const byMatricola = (a, b) => (a.matricola || "").localeCompare(b.matricola || "", "it", { numeric: true });
    const byRecent = (a, b) => (b.created_at || "").localeCompare(a.created_at || "");

    switch (sortBy) {
      case "year_name":       list.sort((a, b) => byYear(a, b) || byName(a, b)); break;
      case "year_first_last": list.sort((a, b) => byYear(a, b) || byFirstLast(a, b)); break;
      case "name_asc":        list.sort(byName); break;
      case "name_desc":       list.sort((a, b) => -byName(a, b)); break;
      case "matricola":       list.sort(byMatricola); break;
      case "recent":
      default:                list.sort(byRecent); break;
    }
    return list;
  }, [students, sortBy]);

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1400px] mx-auto">
      <PageHeader
        title={track === "modulare" ? "Studenti — Modulare" : "Studenti"}
        subtitle="Anagrafica studenti con matricola, contatti, percorso di studi e documenti."
        actions={
          <div className="flex items-center gap-2">
            <Button
              data-testid="student-export-pdf"
              onClick={exportPdf}
              variant="outline"
              className="rounded-full"
              title="Scarica elenco filtrato in PDF A4"
            >
              <Download size={15} className="mr-1.5"/> Esporta PDF
            </Button>
            <Button
              data-testid="student-add-btn"
              onClick={() => setCreateOpen(true)}
              className="rounded-full bg-[#1C352D] hover:bg-[#264A3E] transition-colors duration-150"
            >
              <Plus size={16} className="mr-1.5" /> Nuovo studente
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
        <Card className="p-5 border border-stone-200 rounded-xl shadow-none">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-stone-500">
            <GraduationCap size={12}/> Totale
          </div>
          <div className="text-2xl font-semibold mt-1">{stats?.total || 0}</div>
        </Card>
        <Card className="p-5 border border-stone-200 rounded-xl shadow-none">
          <div className="text-[10px] uppercase tracking-widest text-stone-500">Attivi</div>
          <div className="text-2xl font-semibold mt-1 text-emerald-700">{countByStatus("attivo")}</div>
        </Card>
        <Card className="p-5 border border-stone-200 rounded-xl shadow-none">
          <div className="text-[10px] uppercase tracking-widest text-stone-500">Aula I</div>
          <div className="text-2xl font-semibold mt-1 text-[#1C352D]">{stats?.by_year?.["1"] || 0}</div>
        </Card>
        <Card className="p-5 border border-stone-200 rounded-xl shadow-none">
          <div className="text-[10px] uppercase tracking-widest text-stone-500">Aula II</div>
          <div className="text-2xl font-semibold mt-1 text-[#1C352D]">{stats?.by_year?.["2"] || 0}</div>
        </Card>
        <Card className="p-5 border border-stone-200 rounded-xl shadow-none">
          <div className="text-[10px] uppercase tracking-widest text-stone-500">Aula III</div>
          <div className="text-2xl font-semibold mt-1 text-[#1C352D]">{stats?.by_year?.["3"] || 0}</div>
        </Card>
        <Card className="p-5 border border-stone-200 rounded-xl shadow-none">
          <div className="text-[10px] uppercase tracking-widest text-stone-500">Res / Mod</div>
          <div className="text-2xl font-semibold mt-1">
            <span className="text-emerald-700">{stats?.by_track?.residenziale || 0}</span>
            <span className="text-stone-400 mx-1 text-lg">/</span>
            <span className="text-orange-700">{stats?.by_track?.modulare || 0}</span>
          </div>
        </Card>
      </div>

      <Card className="border border-stone-200 rounded-xl shadow-none">
        <div className="p-4 flex flex-col md:flex-row md:items-center gap-3 border-b border-stone-100">
          <div className="relative flex-1 max-w-md">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input
              data-testid="student-search"
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca per nome, matricola, email, CF…"
              className="pl-9"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="student-status-filter"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              <SelectItem value="attivo">Attivi</SelectItem>
              <SelectItem value="sospeso">Sospesi</SelectItem>
              <SelectItem value="ritirato">Ritirati</SelectItem>
              <SelectItem value="diplomato">Diplomati</SelectItem>
            </SelectContent>
          </Select>
          <Select value={yearFilter} onValueChange={setYearFilter}>
            <SelectTrigger className="w-32" data-testid="student-year-filter"><SelectValue placeholder="Anno"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli anni</SelectItem>
              <SelectItem value="1">I anno</SelectItem>
              <SelectItem value="2">II anno</SelectItem>
              <SelectItem value="3">III anno</SelectItem>
            </SelectContent>
          </Select>
          {!track && <Select value={trackFilter} onValueChange={setTrackFilter}>
            <SelectTrigger className="w-40" data-testid="student-track-filter"><SelectValue placeholder="Percorso"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i percorsi</SelectItem>
              <SelectItem value="residenziale">Residenziale</SelectItem>
              <SelectItem value="modulare">Modulare</SelectItem>
            </SelectContent>
          </Select>}
          <Select value={genderFilter} onValueChange={setGenderFilter}>
            <SelectTrigger className="w-32" data-testid="student-gender-filter"><SelectValue placeholder="Sesso"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti</SelectItem>
              <SelectItem value="M">Uomini</SelectItem>
              <SelectItem value="F">Donne</SelectItem>
              <SelectItem value="altro">Altro</SelectItem>
            </SelectContent>
          </Select>
          <Select value={insFilter} onValueChange={setInsFilter}>
            <SelectTrigger className="w-44" data-testid="student-insurance-filter"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Assicurazione: tutti</SelectItem>
              <SelectItem value="si" data-testid="student-insurance-filter-si">Con assicurazione</SelectItem>
              <SelectItem value="no" data-testid="student-insurance-filter-no">Senza assicurazione</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-52" data-testid="student-sort"><SelectValue placeholder="Ordina per"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="year_name">Anno + Cognome, Nome</SelectItem>
              <SelectItem value="year_first_last">Anno + Nome Cognome</SelectItem>
              <SelectItem value="name_asc">Cognome, Nome (A–Z)</SelectItem>
              <SelectItem value="name_desc">Cognome, Nome (Z–A)</SelectItem>
              <SelectItem value="matricola">Matricola</SelectItem>
              <SelectItem value="recent">Iscrizione recente</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50/60 hover:bg-stone-50/60">
                <TableHead className="uppercase text-[10px] tracking-widest">Matricola</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest">Studente</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest">Anno</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest">Percorso</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest">Stato</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest">Assicurazione</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest">Iscrizione</TableHead>
                <TableHead className="w-12"/>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && <TableRow><TableCell colSpan={8} className="text-center py-10 text-stone-400">Caricamento…</TableCell></TableRow>}
              {!isLoading && sortedStudents.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center py-10 text-stone-400">
                  Nessuno studente. Crea il primo con il pulsante “Nuovo studente”.
                </TableCell></TableRow>
              )}
              {sortedStudents.map((s, i) => (
                <TableRow
                  key={s.id}
                  data-testid={`student-row-${s.id}`}
                  className="animate-in-row hover:bg-stone-50/50"
                  style={{ animationDelay: `${i * 10}ms` }}
                >
                  <TableCell className="mono text-xs text-stone-600">{s.matricola}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                    <StudentAvatar studentId={s.id} photoPath={s.photo_path} name={fullName(s)} testId={`student-avatar-${s.id}`}/>
                    <div className="min-w-0">
                    <Link to={`${track === "modulare" ? "/modulare" : "/scuola"}/studenti/${s.id}`} className="font-medium hover:text-[#1C352D]">
                      {fullName(s)}
                    </Link>
                    <div className="text-[11px] text-stone-500">{s.email || s.city || "—"}</div>
                    </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {s.year_level ? (
                      <div className="flex flex-col gap-0.5">
                        <Badge className="bg-[#1C352D] text-white rounded-full text-[10px] px-2 py-0.5 w-fit">{YEAR_LABEL[s.year_level]}</Badge>
                        {s.academic_year && <span className="text-[10px] text-stone-500 mono">{s.academic_year}</span>}
                      </div>
                    ) : (
                      s.academic_year
                        ? <span className="text-[10px] text-stone-500 mono">{s.academic_year}</span>
                        : <span className="text-stone-400 text-xs">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    {s.track_type ? (
                      <Badge className={`rounded-full text-[10px] font-medium border ${s.track_type === "residenziale" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-orange-50 text-orange-800 border-orange-200"}`}>
                        {TRACK_SHORT[s.track_type]}
                      </Badge>
                    ) : <span className="text-stone-400 text-xs">—</span>}
                    {s.course_track && <div className="text-[11px] text-stone-500 mt-0.5">{s.course_track}</div>}
                  </TableCell>
                  <TableCell><StudentStatus status={s.status}/></TableCell>
                  <TableCell><InsuranceBadge ok={s.has_insurance} testId={`student-insurance-${s.id}`}/></TableCell>
                  <TableCell className="text-sm">{fmtDate(s.enrollment_date)}</TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          data-testid={`student-actions-${s.id}`}
                          className="p-1.5 rounded-md hover:bg-stone-100 text-stone-500 hover:text-[#1C352D] transition-colors duration-150"
                        >
                          <MoreHorizontal size={16}/>
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-40 bg-white">
                        <DropdownMenuItem data-testid={`student-view-${s.id}`} onClick={() => navigate(`${track === "modulare" ? "/modulare" : "/scuola"}/studenti/${s.id}`)}>
                          <Eye size={14} className="mr-2"/> Apri scheda
                        </DropdownMenuItem>
                        <DropdownMenuItem data-testid={`student-edit-${s.id}`} onClick={() => setEditStudent(s)}>
                          <Pencil size={14} className="mr-2"/> Modifica
                        </DropdownMenuItem>
                        {!s.account_activated && s.email && (
                          <DropdownMenuItem
                            data-testid={`student-activate-${s.id}`}
                            onClick={() => activateMut.mutate(s.id)}
                          >
                            <KeyRound size={14} className="mr-2"/> Attiva account portale
                          </DropdownMenuItem>
                        )}
                        {s.account_activated && (
                          <DropdownMenuItem disabled>
                            <KeyRound size={14} className="mr-2"/> Account attivato ✓
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator/>
                        <DropdownMenuItem
                          data-testid={`student-delete-${s.id}`}
                          className="text-red-700 focus:text-red-800 focus:bg-red-50"
                          onClick={() => setDeleteStudent(s)}
                        >
                          <Trash2 size={14} className="mr-2"/> Elimina
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <StudentFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        mode="create"
        submitting={createMut.isPending}
        onSubmit={(payload) => createMut.mutate(payload)}
      />

      <StudentFormDialog
        open={!!editStudent}
        onOpenChange={(v) => { if (!v) setEditStudent(null); }}
        mode="edit"
        initialStudent={editStudent}
        submitting={editMut.isPending}
        onSubmit={(payload) => editMut.mutate({ id: editStudent.id, payload })}
      />

      <AlertDialog open={!!deleteStudent} onOpenChange={(v) => { if (!v) setDeleteStudent(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare {deleteStudent ? fullName(deleteStudent) : "questo studente"}?</AlertDialogTitle>
            <AlertDialogDescription>
              L&apos;operazione è irreversibile. La scheda studente verrà rimossa; i documenti caricati resteranno archiviati come cancellati.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="student-delete-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="student-delete-confirm"
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => deleteStudent && deleteMut.mutate(deleteStudent.id)}
            >
              Elimina definitivamente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!activated} onOpenChange={(v) => { if (!v) setActivated(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Account portale attivato</AlertDialogTitle>
            <AlertDialogDescription>
              Lo studente riceverà un&apos;email con le credenziali. Copia queste dettagli come backup:
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="p-4 bg-stone-50 rounded-lg space-y-1.5 text-sm">
            <div><b>Email:</b> <span className="mono">{activated?.email}</span></div>
            <div><b>Password provvisoria:</b> <code className="bg-white px-2 py-0.5 rounded border border-stone-200 mono">{activated?.temp_password}</code></div>
            <div className="text-[11px] text-stone-500 mt-2">Lo studente dovrà impostare una nuova password al primo accesso.</div>
          </div>
          <AlertDialogFooter>
            <AlertDialogAction data-testid="activated-ok" onClick={() => setActivated(null)} className="bg-[#1C352D] hover:bg-[#264A3E]">Ho capito</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
